import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { Loader2, Mail, Lock, CheckCircle2, ShieldAlert } from 'lucide-react';
import { NeoCard, NeoButton, NeoInput } from '../NexusUI';
import { ThemeToggle } from '../theme-toggle';
import { MfaEnrollment } from './MfaEnrollment';
import { MfaChallenge } from './MfaChallenge';

export function AuthScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const reason = searchParams.get('reason');
  const errorParam = searchParams.get('error');
  const isPasswordChanged = reason === 'password_changed';
  const isSessionExpired = reason === 'session_expired';
  const isAdminParam = searchParams.get('admin') === 'true' || searchParams.get('mode') === 'admin';

  const { session, signInWithMicrosoft, signIn, mfaStatus, refreshMfaStatus, signOut, authError, clearAuthError } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  
  const [showAdminLogin, setShowAdminLogin] = useState(isPasswordChanged || isAdminParam || false);

  // If already authenticated, redirect to target page or root / dashboard immediately
  useEffect(() => {
    if (session) {
      const fromPath = (location.state as any)?.from?.pathname || searchParams.get('redirect') || searchParams.get('returnUrl');
      if (fromPath && fromPath !== '/login') {
        navigate(fromPath, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [session, navigate, location.state]);

  useEffect(() => {
    if (isPasswordChanged) {
      setShowAdminLogin(true);
      setInfoMessage((location.state as any)?.message || 'Password changed successfully. Please log in again using your new password.');
    } else if (isSessionExpired) {
      setError('Your session has expired. Please sign in again.');
      toast.push('Unauthorized Access: Your session has expired. Please sign in again.', 'error');
    }
  }, [isPasswordChanged, isSessionExpired]);

  useEffect(() => {
    if (errorParam === 'unprovisioned') {
      const msg = 'Your account is authenticated but has not been provisioned for Compass Ticketing.';
      setError(msg);
      toast.push(`Unauthorized Access: ${msg}`, 'error');
    } else if (errorParam === 'disabled') {
      const msg = 'Your account has been disabled. Please contact your administrator.';
      setError(msg);
      toast.push(`Unauthorized Access: ${msg}`, 'error');
    } else if (errorParam === 'suspended') {
      const msg = 'Your account is suspended. Please contact your administrator.';
      setError(msg);
      toast.push(`Unauthorized Access: ${msg}`, 'error');
    } else if (errorParam === 'sso_failed') {
      const msg = 'Microsoft sign-in was unsuccessful. Please try again.';
      setError(msg);
      toast.push(`Unauthorized Access: ${msg}`, 'error');
    }

    // Check OAuth hash errors (e.g. #error=...&error_description=...)
    if (window.location.hash) {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashErrDesc = hashParams.get('error_description');
        const hashErr = hashParams.get('error');
        if (hashErrDesc || hashErr) {
          const msg = decodeURIComponent(hashErrDesc || hashErr || 'Authentication failed. Please try again.');
          setError(msg);
          toast.push(`Unauthorized Access: ${msg}`, 'error');
        }
      } catch (e) {}
    }
  }, [errorParam]);

  useEffect(() => {
    if (authError) {
      setError(authError);
      toast.push(`Unauthorized Access: ${authError}`, 'error');
    }
  }, [authError]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      const msg = 'Please enter both your email address and password.';
      setError(msg);
      toast.push(`Unauthorized Access: ${msg}`, 'error');
      return;
    }
    setBusy(true);
    setError(null);
    setInfoMessage(null);
    clearAuthError();
    
    const err = await signIn(email, password);
    if (err) {
      setShowAdminLogin(true);
      const isInvalid = 
        err.toLowerCase().includes('invalid login credentials') || 
        err.toLowerCase().includes('invalid credentials') ||
        err.toLowerCase().includes('invalid password') ||
        err.toLowerCase().includes('user not found');
      
      const errorMsg = isInvalid 
        ? 'Invalid email or password. Please verify your credentials.'
        : err;

      setError(errorMsg);
      toast.push(`Unauthorized Access: ${errorMsg}`, 'error');
      setBusy(false);
    } else {
      setBusy(false);
      const fromPath = (location.state as any)?.from?.pathname;
      if (fromPath && fromPath !== '/login') {
        navigate(fromPath, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  };
  
  const handleMicrosoftSignIn = async () => {
    setBusy(true);
    setError(null);
    setInfoMessage(null);
    clearAuthError();
    
    const err = await signInWithMicrosoft();
    if (err) {
      const errorMsg = 'Microsoft sign-in was unsuccessful. Please try again.';
      setError(errorMsg);
      toast.push(`Unauthorized Access: ${errorMsg}`, 'error');
    }
    
    setBusy(false);
  };


  return (
    <div className="relative flex min-h-screen items-center justify-center bg-theme p-4 transition-colors">
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      <NeoCard className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="rounded-2xl p-2.5 bg-white/95 dark:bg-white/90 shadow-md transition-all">
            <img 
              src="/rezilyens.png" 
              alt="Rezilyens" 
              className="h-16 object-contain cursor-pointer select-none transition-transform hover:scale-105 active:scale-95"
              onDoubleClick={() => setShowAdminLogin(prev => !prev)}
              title="Double-click for Email & Password Sign In"
            />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-theme mt-2 mb-4">Rezilyens Enterprise</h1>
        </div>

        {infoMessage && (
          <div className="mb-6 rounded-xl neo-inset border border-emerald-500/30 bg-emerald-500/10 p-4 text-xs text-emerald-600 dark:text-emerald-400 text-center font-medium flex items-start gap-2.5 animate-in fade-in duration-200">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-emerald-500" />
            <span className="text-left">{infoMessage}</span>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-xl neo-inset border border-red-500/40 bg-red-500/10 p-3.5 text-sm text-red-500 font-medium flex items-start gap-3 animate-in fade-in duration-200">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-500/20 text-red-500 mt-0.5">
              <ShieldAlert className="h-4 w-4" />
            </div>
            <div className="flex flex-col text-left">
              <span className="font-semibold text-red-600 dark:text-red-400 text-xs uppercase tracking-wider">Unauthorized Access</span>
              <span className="text-xs text-red-500/90 mt-0.5 leading-relaxed">{error}</span>
            </div>
          </div>
        )}
        
        {mfaStatus === 'pending' && (
          <MfaEnrollment onEnrolled={async () => { await refreshMfaStatus(); navigate('/', { replace: true }); }} onCancel={signOut} />
        )}
        
        {mfaStatus === 'enrolled' && (
          <MfaChallenge onVerified={async () => { await refreshMfaStatus(); navigate('/', { replace: true }); }} onCancel={signOut} />
        )}

        {mfaStatus !== 'pending' && mfaStatus !== 'enrolled' && (
          <form onSubmit={handleEmailSignIn} className="space-y-6">
          {!showAdminLogin ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={handleMicrosoftSignIn}
                disabled={busy}
                className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-card px-4 py-4 text-base font-bold text-foreground shadow-sm transition-all hover:bg-muted active:scale-95"
              >
                {busy ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                )}
                Sign in with Microsoft
              </button>
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="space-y-2 mb-6">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-2">Email Address</label>
                <NeoInput
                  type="email"
                  required
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  icon={<Mail className="h-5 w-5" />}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between ml-2 mr-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Password</label>
                  <a href="#" className="text-xs font-bold text-primary hover:underline transition-colors">Forgot password?</a>
                </div>
                <div className="relative">
                  <NeoInput
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    icon={<Lock className="h-5 w-5" />}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-5 text-muted-foreground hover:text-theme transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between ml-2 mb-6 mt-4">
                <div className="flex items-center space-x-3">
                  <input 
                    type="checkbox" 
                    id="remember" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)] bg-transparent"
                  />
                  <label htmlFor="remember" className="text-sm font-medium text-theme cursor-pointer select-none">
                    Remember me
                  </label>
                </div>
              </div>

              <NeoButton
                type="submit"
                variant="primary"
                disabled={busy || !email || !password}
                className="w-full flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="h-5 w-5 animate-spin" />}
                Sign In
              </NeoButton>
              
              <div className="mt-6 text-center">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAdminLogin(false);
                    setError(null);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel and return to SSO
                </button>
              </div>
            </div>
          )}
        </form>
        )}
      </NeoCard>
    </div>
  );
}
